/** @jsx jsx */
import { jsx } from '@theme-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import countries from 'src/shared/helpers/countries.json';
import { WriteIcon } from 'src/shared/components/icons';
import { Translation, translations } from 'src/shared/translation';
import { Stack } from 'src/shared/components/stack';
import { Button } from 'src/shared/components/button';
import { useCustomForm } from 'src/shared/hooks/use-custom-form';
import { FormProvider } from 'src/shared/contexts/form-context';
import { CategorySelect, CountrySelect, FormInput } from 'src/shared/components/form-elements';
import { useTranslation } from 'src/shared/translation';
import { getUserEventAttr } from 'src/shared/helpers/tracking-user-events';

import { EMPTY_OBJECT } from '../../../../shared/helpers/object/empty-object';
import { getCategoryById } from '../../../api/get-categories';
import { useAppConfig } from '../../../../shared/contexts/app-config';

import { currentQueryArgs, getInitialValuesFromUrl, getSchema, inputsIdsMap } from './utils';

const transl = translations.nearmecheck.start_page.form;

export const ManualInputForm = () => {
    const { t } = useTranslation();
    const {
        language,
        publicKey,
        hideGoogleConnectFlow,
        emailFieldVisibility,
        phoneNumberFieldVisibility,
    } = useAppConfig();

    const shouldShowOopsButton = useShouldShowOopsButton();
    const history = useHistory();
    const schema = useMemo(
        () => getSchema(t, emailFieldVisibility, phoneNumberFieldVisibility),
        [t, emailFieldVisibility, phoneNumberFieldVisibility]
    );
    const [prefilledCategory, setPrefilledCategory] = useState<{ name: string; id: number }>();

    function handleSubmit(values: { [k: string]: string }) {
        const formValues = Object.entries(values);

        // map form values into queryString to name=value to redirect to results page
        const queryString = formValues
            .map((item) => {
                const queryStringName = inputsIdsMap[item[0] as keyof typeof inputsIdsMap];
                return `${queryStringName}=${encodeURIComponent(item[1])}`;
            })
            .join('&');

        history.push(`/results?${queryString}`);
    }

    const queryMap = currentQueryArgs(history.location.search);

    useEffect(() => {
        if (queryMap.categoryId) {
            getCategoryById(parseInt(queryMap.categoryId, 10), language, publicKey).then(
                (res: any) => {
                    setPrefilledCategory(res[0]);
                }
            );
        }
    }, [queryMap.categoryId, language, publicKey]);

    const formMethods = useCustomForm({
        initialValues: getInitialValuesFromUrl(queryMap) || EMPTY_OBJECT,
        onSubmit: (values: { [k: string]: string }) => {
            handleSubmit(values);
        },
        schema,
    });

    const countryValue = formMethods.values['manual-form-country-select'] as keyof typeof countries;

    const shouldUseZipCode = countryValue ? countries[countryValue].usesZipCode : true;

    const titleKey = hideGoogleConnectFlow ? transl.title.no_gmb : transl.title.with_gmb;

    return (
        <FormProvider value={formMethods}>
            <form>
                <div
                    sx={{
                        display: 'flex',
                        marginBottom: 5,
                    }}
                >
                    <div
                        sx={{
                            paddingTop: 1,
                            paddingRight: 2,
                        }}
                    >
                        <WriteIcon sx={{ display: 'block' }} />
                    </div>
                    <p sx={{ margin: 0 }}>
                        <Translation code={titleKey} />
                    </p>
                </div>
                <Stack space={5}>
                    <CategorySelect
                        id="manual-form-category-select"
                        key={
                            prefilledCategory
                                ? 'prefilled-form-category-select'
                                : 'manual-form-category-select'
                        }
                        defaultValue={
                            prefilledCategory
                                ? { label: prefilledCategory.name, value: prefilledCategory.id }
                                : undefined
                        }
                        userEvent="nmc-start-category"
                    />

                    <CountrySelect
                        id="manual-form-country-select"
                        data-testid="manual-form-country-select"
                        userEvent="nmc-start-country"
                        onChange={(option) => {
                            formMethods.handleChange(
                                (option as any)?.value,
                                'manual-form-country-select'
                            );
                        }}
                        errorMessage={formMethods.errors['manual-form-country-select']?.message}
                        selectedValue={formMethods.values['manual-form-country-select']}
                    />

                    <FormInput
                        id="manual-form-company-name"
                        label={<Translation code={transl.company_name.label} />}
                        placeholder={transl.company_name.placeholder}
                        {...getUserEventAttr('nmc-start-location-name')}
                    />

                    <FormInput
                        id="manual-form-street-and-number"
                        label={<Translation code={transl.address.label} />}
                        placeholder={transl.address.placeholder}
                        {...getUserEventAttr('nmc-start-address')}
                    />

                    {shouldUseZipCode ? (
                        <FormInput
                            id="manual-form-zip-code"
                            label={<Translation code={transl.zip_code.label} />}
                            placeholder={transl.zip_code.placeholder}
                            {...getUserEventAttr('nmc-start-zip')}
                        />
                    ) : (
                        <FormInput
                            id="manual-form-city"
                            label={<Translation code={transl.city.label} />}
                            placeholder={transl.city.placeholder}
                            {...getUserEventAttr('nmc-start-location-name')}
                        />
                    )}

                    {emailFieldVisibility !== 'hidden' && (
                        <FormInput
                            id="manual-form-email"
                            label={
                                <React.Fragment>
                                    <Translation code={transl.email.label} /> &nbsp;
                                    {emailFieldVisibility === 'optional' && (
                                        <Translation
                                            sx={{ color: 'nearMeCheck.grey.3' }}
                                            code={transl.optional}
                                        />
                                    )}
                                </React.Fragment>
                            }
                            placeholder={transl.email.placeholder}
                            {...getUserEventAttr('nmc-start-location-email')}
                        />
                    )}

                    {phoneNumberFieldVisibility !== 'hidden' && (
                        <FormInput
                            id="manual-form-phone-number"
                            label={
                                <React.Fragment>
                                    <Translation code={transl.phone_number.label} /> &nbsp;
                                    {phoneNumberFieldVisibility === 'optional' && (
                                        <Translation
                                            sx={{ color: 'nearMeCheck.grey.3' }}
                                            code={transl.optional}
                                        />
                                    )}
                                </React.Fragment>
                            }
                            placeholder={transl.phone_number.placeholder}
                            {...getUserEventAttr('nmc-start-location-phone_number')}
                        />
                    )}

                    <div sx={{ paddingTop: 3 }}>
                        <Button
                            block
                            secondary
                            type="submit"
                            onClick={formMethods.handleSubmit}
                            {...getUserEventAttr('nmc-start-incomplete-results')}
                        >
                            <Translation code={transl.submit_button_label} />
                        </Button>
                    </div>
                    {shouldShowOopsButton && <OopsButton />}
                </Stack>
            </form>
        </FormProvider>
    );
};

function OopsButton() {
    const [oops, setOops] = useState(false);

    if (oops) {
        throw Error('Oops');
    }

    return (
        <Button block onClick={() => setOops(true)}>
            {'💣'}
        </Button>
    );
}

function useShouldShowOopsButton() {
    const location = useLocation();

    // The oops parameter is used to trigger a fatal error within React to test our error handling in production.
    return new URLSearchParams(location?.search).get('oops') === 'true';
}
